import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

import { getInitials } from '@/utils/identity'

import { AccountNavigation } from './AccountNavigation'
import { StatusBanner } from './StatusBanner'

import type { ReactNode } from 'react'

type AccountShellProps = {
    profileName?: string
    profileEmail?: string
    profilePictureUrl?: string | null
    onLogout?: () => void
    signingOut?: boolean
    title: string
    description: string
    status?: {
        tone: 'success' | 'error'
        message: string
    } | null
    children: ReactNode
}

export const AccountShell = ({
    profileName,
    profileEmail,
    profilePictureUrl,
    onLogout,
    signingOut,
    title,
    description,
    status,
    children,
}: AccountShellProps) => {
    const { t } = useTranslation()

    return (
        <div className="bg-account-page text-account-strong min-h-screen font-sans">
            {profileName && (
                <header className="border-account-divider border-b">
                    <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-5 sm:px-6 sm:py-6">
                        <div className="flex min-w-0 items-center gap-3">
                            {profilePictureUrl ? (
                                <img
                                    src={profilePictureUrl}
                                    alt=""
                                    className="h-10 w-10 shrink-0 rounded-full object-cover"
                                />
                            ) : (
                                <div className="bg-account-accent-soft text-account-accent-text flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                    {getInitials(profileName)}
                                </div>
                            )}
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold" title={profileName}>
                                    {profileName}
                                </p>
                                {profileEmail && (
                                    <p className="text-account-secondary mt-0.5 truncate text-xs" title={profileEmail}>
                                        {profileEmail}
                                    </p>
                                )}
                            </div>
                        </div>
                        {onLogout && (
                            <button
                                type="button"
                                onClick={onLogout}
                                disabled={signingOut}
                                className="text-account-secondary hover:text-account-strong inline-flex shrink-0 items-center gap-2 rounded py-2 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                            >
                                <ArrowRightStartOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
                                {t('shell.signOut')}
                            </button>
                        )}
                    </div>
                </header>
            )}
            <main className="mx-auto flex max-w-3xl flex-col gap-8 px-4 py-7 sm:px-6 sm:py-10">
                {profileName && <AccountNavigation />}
                {(title || description) && (
                    <div>
                        {title && <h1 className="text-account-strong text-2xl font-bold sm:text-3xl">{title}</h1>}
                        {description && <p className="text-account-secondary mt-2 text-sm">{description}</p>}
                    </div>
                )}
                {status && <StatusBanner tone={status.tone} message={status.message} />}
                {children}
            </main>
        </div>
    )
}
